(function () {
  'use strict';

  class ZoomImages extends HTMLElement {
    constructor() {
      super();
      this.container = this.closest('[data-section-id]');
      this.zoomCaptions = this.container ? this.container.querySelector('[data-zoom-caption]') : null;
      this.thumbsContainer = document.querySelector('.pswp__thumbs');
    }

    connectedCallback() {
      this.getImages().forEach((image) => {
        image.addEventListener('click', (event) => {
          event.preventDefault();
          const productImages = image.closest('product-images');

          if (productImages && productImages.classList.contains('is-dragging')) return;

          this.createZoom(image);
          window.a11y.lastElement = image;
        });

        image.addEventListener('keyup', (event) => {
          if (event.code !== 'Enter') return;

          event.preventDefault();
          this.createZoom(image);
          window.a11y.lastElement = image;
        });
      });
    }

    getImages() {
      return Array.from(this.querySelectorAll('[data-zoom-image]'));
    }

    isMobileGallery(productImagesElement) {
      if (!productImagesElement) return false;

      return (
        productImagesElement.classList.contains('product__images--mobile-slider') ||
        productImagesElement.classList.contains('product__images--mobile-thumbs')
      );
    }

    getVisibleThumbMediaIds() {
      if (!this.container) return [];

      const thumbItems = Array.from(this.container.querySelectorAll('product-thumbs [data-thumb-item]'));
      if (!thumbItems.length) return [];

      const visibleItems = thumbItems.filter((item) => {
        if (item.hidden || item.getAttribute('aria-hidden') === 'true') return false;
        return window.getComputedStyle(item).display !== 'none';
      });

      return visibleItems
        .map((item) => item.querySelector('[data-thumb-link]')?.getAttribute('data-media-id'))
        .filter(Boolean);
    }

    getActiveVariantTitle(images) {
      if (!images.length) return '';

      const productImagesElement = images[0].closest('product-images');
      if (!productImagesElement) return '';

      const activeMediaId = productImagesElement.getAttribute('data-active-media') || '';

      let activeSlide = null;
      if (activeMediaId) {
        activeSlide = productImagesElement.querySelector(`[data-slide][data-media-id="${activeMediaId}"]`);
      }

      if (!activeSlide) {
        activeSlide = productImagesElement.querySelector('[data-slide].media--active');
      }

      return (activeSlide && activeSlide.getAttribute('data-variant-title')) || '';
    }

    getVariantScopedImages(images) {
      if (!images.length) return [];

      // 1) Primary source: currently visible thumbs (theme already filters these by selected variant).
      const visibleThumbMediaIds = this.getVisibleThumbMediaIds();
      if (visibleThumbMediaIds.length) {
        const mediaSet = new Set(visibleThumbMediaIds);
        const byThumbs = images.filter((image) => {
          const slide = image.closest('[data-slide]');
          const mediaId = slide ? slide.getAttribute('data-media-id') : '';
          return mediaId && mediaSet.has(mediaId);
        });

        if (byThumbs.length) return byThumbs;
      }

      // 2) Fallback: match active slide variant title.
      const activeVariantTitle = this.getActiveVariantTitle(images);
      if (!activeVariantTitle) return [];

      const byVariantTitle = images.filter((image) => {
        const slide = image.closest('[data-slide]');
        if (!slide) return false;

        const imageVariantTitle = slide.getAttribute('data-variant-title');
        // Keep generic images (no variant title) + selected variant images.
        return !imageVariantTitle || imageVariantTitle === activeVariantTitle;
      });

      return byVariantTitle;
    }

    getVisibleImages(images) {
      if (!images.length) return [];

      const variantScoped = this.getVariantScopedImages(images);
      if (variantScoped.length) return variantScoped;

      const productImagesElement = images[0].closest('product-images');
      if (this.isMobileGallery(productImagesElement)) {
        return images;
      }

      return images.filter((image) => {
        const slide = image.closest('[data-slide]');
        return !slide || !slide.classList.contains('media--hidden');
      });
    }

    renderThumbsFromTemplate(images) {
      if (!this.thumbsContainer) return;

      const template = this.container ? this.container.querySelector('[data-pswp-thumbs-template]') : null;
      if (!template) return;

      this.thumbsContainer.innerHTML = template.innerHTML;

      const mediaIds = new Set(
        images
          .map((image) => {
            const slide = image.closest('[data-slide]');
            return slide && slide.hasAttribute('data-media-id') ? slide.getAttribute('data-media-id') : null;
          })
          .filter(Boolean)
      );

      if (mediaIds.size) {
        Array.from(this.thumbsContainer.children).forEach((thumb) => {
          const mediaId = thumb.getAttribute('data-media-id');
          if (mediaId && !mediaIds.has(mediaId)) {
            thumb.remove();
          }
        });
      }
    }

    createZoom(targetImage) {
      const allImages = this.getImages();
      const visibleImages = this.getVisibleImages(allImages);
      const images = visibleImages.length ? visibleImages : allImages;

      if (!images.length) return;

      const index = images.indexOf(targetImage);
      const initialIndex = index > -1 ? index : 0;

      const items = images.map((image) => {
        const src = image.getAttribute('data-image-src');
        return {
          src: src,
          w: parseInt(image.getAttribute('data-image-width'), 10) || 0,
          h: parseInt(image.getAttribute('data-image-height'), 10) || 0,
          msrc: src,
        };
      });

      const colorScheme = this.getColorScheme();
      const options = {
        history: false,
        focus: false,
        index: initialIndex,
        mainClass: `pswp-zoom-gallery${items.length === 1 ? ' pswp-zoom-gallery--single' : ''}${colorScheme ? ` ${colorScheme}` : ''}`,
        showHideOpacity: true,
        howAnimationDuration: 150,
        hideAnimationDuration: 250,
        closeOnScroll: false,
        closeOnVerticalDrag: false,
        captionEl: true,
        closeEl: true,
        closeElClasses: ['caption-close', 'title'],
        tapToClose: false,
        clickToCloseNonZoomable: false,
        maxSpreadZoom: 2,
        loop: true,
        spacing: 0,
        allowPanToNext: true,
        pinchToClose: false,
        addCaptionHTMLFn: (item, captionEl, isFake) => this.zoomCaption(item, captionEl, isFake),
        getThumbBoundsFn: (photoSwipeIndex) => {
          const image = images[photoSwipeIndex] || images[0];
          const scrollY = window.scrollY || document.documentElement.scrollTop;
          const rect = image.getBoundingClientRect();

          return {
            x: rect.left,
            y: rect.top + scrollY,
            w: rect.width,
          };
        },
      };

      this.renderThumbsFromTemplate(images);
      new window.theme.LoadPhotoswipe(items, options);
    }

    zoomCaption(item, captionEl) {
      let caption = '';
      const element = captionEl.children[0];

      if (this.zoomCaptions) {
        caption = this.zoomCaptions.innerHTML;

        if (this.zoomCaptions.closest('.variant--soldout')) {
          element.classList.add('variant--soldout');
        } else {
          element.classList.remove('variant--soldout');
        }

        if (this.zoomCaptions.closest('.variant--unavailable')) {
          element.classList.add('variant--unavailable');
        } else {
          element.classList.remove('variant--unavailable');
        }
      }

      element.innerHTML = caption;
      return false;
    }

    getColorScheme() {
      const parent = this.closest('product-info');
      return parent ? Array.from(parent.classList).find((name) => name.startsWith('color-scheme')) : '';
    }
  }

  if (!customElements.get('zoom-images')) {
    customElements.define('zoom-images', ZoomImages);
  }
})();
